/**
 * KPISheet Component
 * 
 * Generic slide-over component for displaying KPI statistics using MUI Drawer.
 * Highly configurable for different use cases (campaigns, tickets, etc.).
 * 
 * @example
 * // Basic usage
 * <KPISheet
 *   isOpen={isOpen}
 *   onClose={onClose}
 *   title="Overview"
 *   totalItems={100}
 *   primaryStats={[
 *     { icon: <TrendingUp />, label: 'Total', value: 100, color: 'primary' },
 *   ]}
 * />
 * 
 * @example
 * // With status breakdown
 * <KPISheet
 *   isOpen={isOpen}
 *   onClose={onClose}
 *   title="Tickets Overview"
 *   totalItems={50}
 *   statusGroups={[
 *     { id: 1, statusName: 'Open', statusColor: '#22c55e', count: 20 },
 *     { id: 2, statusName: 'Closed', statusColor: '#6b7280', count: 30 },
 *   ]}
 * />
 */

import React from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  LinearProgress,
  Paper,
  Fade,
} from '@mui/material';
import {
  Close as CloseIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';

// =============================================================================
// TYPES
// =============================================================================

export type StatColor =
  | 'primary'
  | 'secondary'
  | 'success'
  | 'warning'
  | 'error'
  | 'info'
  | 'blue'
  | 'emerald'
  | 'amber'
  | 'rose'
  | 'purple'
  | 'teal'
  | 'cyan'
  | 'gray';

export interface StatItem {
  /** Icon to display */
  icon: React.ReactNode;
  /** Label text */
  label: string;
  /** Numeric value */
  value: number;
  /** Color theme */
  color: StatColor;
}

export interface StatusGroup {
  /** Unique identifier */
  id: number | string;
  /** Status display name */
  statusName: string;
  /** Status color (hex) */
  statusColor: string;
  /** Count of items in this status */
  count: number;
}

export interface RateCardConfig {
  /** Label for the rate */
  label: string;
  /** Current value */
  value: number;
  /** Total for percentage calculation */
  total: number;
  /** Color for the rate display */
  color: string;
}

export interface KPISheetProps {
  /** Whether the sheet is open */
  isOpen: boolean;
  /** Close handler */
  onClose: () => void;
  /** Title text */
  title: string;
  /** Header icon */
  icon?: React.ReactNode;
  /** Header background color (MUI color or gradient) */
  headerColor?: string;
  /** Status groups for breakdown section */
  statusGroups?: StatusGroup[];
  /** Total items count */
  totalItems: number;
  /** Primary stat cards (shown in header) */
  primaryStats?: StatItem[];
  /** Secondary stat cards (shown in content) */
  secondaryStats?: StatItem[];
  /** Entity name for labels (e.g., "campaigns", "tickets") */
  entityName?: string;
  /** Custom header content */
  headerContent?: React.ReactNode;
  /** Custom footer content */
  footerContent?: React.ReactNode;
  /** Rate card configuration */
  rateCard?: RateCardConfig;
  /** Sheet width */
  width?: number | string;
}

// =============================================================================
// COLOR MAPPINGS
// =============================================================================

const colorMap: Record<StatColor, { bg: string; text: string; border: string }> = {
  primary: { bg: '#eff6ff', text: '#2563eb', border: '#bfdbfe' },
  secondary: { bg: '#f3f4f6', text: '#4b5563', border: '#d1d5db' },
  success: { bg: '#ecfdf5', text: '#059669', border: '#a7f3d0' },
  warning: { bg: '#fffbeb', text: '#d97706', border: '#fde68a' },
  error: { bg: '#fef2f2', text: '#c00035', border: '#fecaca' },
  info: { bg: '#ecfeff', text: '#0891b2', border: '#a5f3fc' },
  blue: { bg: '#eff6ff', text: '#2563eb', border: '#bfdbfe' },
  emerald: { bg: '#ecfdf5', text: '#059669', border: '#a7f3d0' },
  amber: { bg: '#fffbeb', text: '#d97706', border: '#fde68a' },
  rose: { bg: '#fff1f2', text: '#e11d48', border: '#fecdd3' },
  purple: { bg: '#faf5ff', text: '#9333ea', border: '#e9d5ff' },
  teal: { bg: '#f0fdfa', text: '#0d9488', border: '#99f6e4' },
  cyan: { bg: '#ecfeff', text: '#0891b2', border: '#a5f3fc' },
  gray: { bg: '#f9fafb', text: '#6b7280', border: '#e5e7eb' },
};

// =============================================================================
// STAT CARD COMPONENT
// =============================================================================

const StatCard: React.FC<StatItem> = ({ icon, label, value, color }) => {
  const colors = colorMap[color] || colorMap.gray;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 2,
        backgroundColor: colors.bg,
        border: `1px solid ${colors.border}`,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, color: colors.text, opacity: 0.8 }}>
        {icon}
        <Typography variant="body2" fontWeight={500}>{label}</Typography>
      </Box>
      <Typography variant="h5" fontWeight={700} sx={{ color: colors.text }}>
        {value.toLocaleString()}
      </Typography>
    </Paper>
  );
};

// =============================================================================
// STATUS ROW COMPONENT
// =============================================================================

interface StatusRowProps {
  group: StatusGroup;
  totalItems: number;
  index: number;
}

const StatusRow: React.FC<StatusRowProps> = ({ group, totalItems, index }) => {
  const percentage = totalItems > 0 ? (group.count / totalItems) * 100 : 0;

  return (
    <Fade in timeout={300 + index * 100}>
      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: 2,
          backgroundColor: '#f9fafb',
          border: '1px solid #f3f4f6',
          '&:hover': { borderColor: '#e5e7eb' },
          transition: 'border-color 0.2s',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: group.statusColor,
                color: 'white',
                fontWeight: 700,
                fontSize: '0.875rem',
              }}
            >
              {group.count}
            </Box>
            <Box>
              <Typography fontWeight={600} color="text.primary">
                {group.statusName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {percentage.toFixed(1)}% of total
              </Typography>
            </Box>
          </Box>

          <Box sx={{ flex: 1, mx: 2, maxWidth: 100 }}>
            <LinearProgress
              variant="determinate"
              value={percentage}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: '#e5e7eb',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 4,
                  backgroundColor: group.statusColor,
                },
              }}
            />
          </Box>
        </Box>
      </Paper>
    </Fade>
  );
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export const KPISheet: React.FC<KPISheetProps> = ({
  isOpen,
  onClose,
  title,
  icon = <TrendingUpIcon />,
  headerColor = 'linear-gradient(135deg, #2563eb 0%, #4f46e5 100%)',
  statusGroups = [],
  totalItems,
  primaryStats = [],
  secondaryStats = [],
  entityName = 'items',
  headerContent,
  footerContent,
  rateCard,
  width = 400,
}) => {
  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: width },
          maxWidth: '100%',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          background: headerColor,
          color: 'white',
          p: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {icon}
            <Typography variant="h6" fontWeight={700}>{title}</Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Primary Stats in Header */}
        {primaryStats.length > 0 && (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
            {primaryStats.slice(0, 2).map((stat, idx) => (
              <Paper
                key={idx}
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  backdropFilter: 'blur(10px)',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'rgba(255,255,255,0.8)', mb: 0.5 }}>
                  {stat.icon}
                  <Typography variant="body2">{stat.label}</Typography>
                </Box>
                <Typography variant="h4" fontWeight={700} sx={{ color: '#fff' }}>
                  {stat.value.toLocaleString()}
                </Typography>
              </Paper>
            ))}
          </Box>
        )}

        {headerContent}
      </Box>

      {/* Content */}
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Secondary Stats */}
        {secondaryStats.length > 0 && (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
            {secondaryStats.map((stat, idx) => (
              <StatCard key={idx} {...stat} />
            ))}
          </Box>
        )}

        {/* Status Breakdown */}
        {statusGroups.length > 0 && (
          <Box>
            <Typography
              variant="overline"
              fontWeight={600}
              color="text.secondary"
              sx={{ display: 'block', mb: 2 }}
            >
              By Status
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {statusGroups.map((group, index) => (
                <StatusRow key={group.id} group={group} totalItems={totalItems} index={index} />
              ))}
            </Box>
          </Box>
        )}

        {statusGroups.length === 0 && !secondaryStats.length && (
          <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
            <Typography>No {entityName} to display</Typography>
          </Box>
        )}

        {/* Rate Card */}
        {rateCard && totalItems > 0 && (
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 2,
              background: `linear-gradient(135deg, ${rateCard.color}15 0%, ${rateCard.color}08 100%)`,
              border: `1px solid ${rateCard.color}20`,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Typography variant="body2" fontWeight={500} sx={{ color: rateCard.color }}>
                {rateCard.label}
              </Typography>
              <Typography variant="h5" fontWeight={700} sx={{ color: rateCard.color }}>
                {((rateCard.value / rateCard.total) * 100).toFixed(0)}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={(rateCard.value / rateCard.total) * 100}
              sx={{
                height: 12,
                borderRadius: 6,
                backgroundColor: `${rateCard.color}20`,
                '& .MuiLinearProgress-bar': {
                  borderRadius: 6,
                  background: `linear-gradient(90deg, ${rateCard.color} 0%, ${rateCard.color}CC 100%)`,
                },
              }}
            />
            <Typography variant="caption" sx={{ color: rateCard.color, mt: 1, display: 'block' }}>
              {rateCard.value.toLocaleString()} out of {rateCard.total.toLocaleString()} {entityName}
            </Typography>
          </Paper>
        )}

        {footerContent}
      </Box>
    </Drawer>
  );
};

export default KPISheet;
